import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Construction from "@mui/icons-material/Construction";
import { useTranslation } from 'react-i18next';
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import { useState, useEffect } from "react";
import { EQUIPMENTS_ENDPOINT, CONTRACT_TERMS_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import EquipmentForm from "../../components/ui/equipmentForm/equipmentForm.component";
import JobForm from "../../components/service/jobForm.component";
import { useParams, useNavigate } from "react-router-dom";
import { useCRUD } from "../../api/crud";
import { useSnackbar } from 'notistack';
import axios from "../../api/axios";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useHasAnyPermission, useHasPermission } from "../../contexts/auth";
import EquipmentCounterForm from "../../components/ui/equipmentCounterForm/equipmentCounterForm.component";

export const EquipmentsDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove } = useCRUD(EQUIPMENTS_ENDPOINT);
  const [updateEquipmentOpened, setUpdateEquipmentOpened] = useState<boolean>(false);
  const [deleteEquipmentOpened, setDeleteEquipmentOpened] = useState<boolean>(false);
  const [addCounterOpened, setAddCounterOpened] = useState<boolean>(false);
  const [createJobOpened, setCreateJobOpened] = useState<boolean>(false);
  const [equipmentClientId, setEquipmentClientId] = useState<string | number | null>(null);
  const canChangeEquipment = useHasPermission("settlement.change_equipment");
  const canDeleteEquipment = useHasPermission("settlement.delete_equipment");
  const canAddCounter = useHasPermission("settlement.add_equipmentcounter");
  const canAddJob = useHasAnyPermission(["service.add_job", "service.add_internal_job"]);

  useEffect(() => {
    if (!id) return;
    axios.get(`${CONTRACT_TERMS_ENDPOINT}?equipment=${id}`).then(({ data }) => {
      const results = Array.isArray(data?.results) ? data.results : data;
      const firstTerm = Array.isArray(results) ? results[0] : null;
      const clientId = firstTerm?.contract?.client_id ?? null;
      setEquipmentClientId(clientId);
    }).catch(() => {});
  }, [id]);

  const handleDeleteEquipment = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t('Equipment deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteEquipmentOpened(false);
      navigate('/equipment-section/equipments');
    } else {
      enqueueSnackbar(t('Failed to delete equipment'), ERROR_SNACKBAR);
    }
  };

  const handleCounterAdded = () => {
    setAddCounterOpened(false);
    window.dispatchEvent(new Event("equipmentCounterAdded"));
  };

  const handleJobCreated = () => {
    setCreateJobOpened(false);
    window.dispatchEvent(new Event("equipmentJobAdded"));
    navigate("/service/jobs");
  };

  const actions: Action[] = [
    {
      id: "new-job",
      icon: <Construction />,
      label: t("New job"),
      onClick: () => {
        setCreateJobOpened(true);
      },
      disabled: !canAddJob,
    },
    {
      id: "add-new-counter",
      icon: <AddIcon />,
      label: t("Add new counter"),
      onClick: () => {
        setAddCounterOpened(true);
      },
      disabled: !canAddCounter,
    },
    {
      icon: <EditIcon />,
      label: t("Update equipment"),
      onClick: () => {
        setUpdateEquipmentOpened(true);
      },
      disabled: !canChangeEquipment,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete equipment"),
      onClick: () => {
        setDeleteEquipmentOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeleteEquipment,
    },
  ];

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this equipment will permanently remove it from the system. This action cannot be undone.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteEquipment}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateEquipmentOpened}
        setOpened={setUpdateEquipmentOpened}
        title={t("Update equipment")}
        content={
          <EquipmentForm
            id={id}
            onSuccess={() => {
              setUpdateEquipmentOpened(false);
              window.dispatchEvent(new Event("equipmentUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={deleteEquipmentOpened}
        setOpened={setDeleteEquipmentOpened}
        title={t("Delete equipment")}
        content={deleteModalContent}
      />
      <CustomModal
        opened={addCounterOpened}
        setOpened={setAddCounterOpened}
        title={t("Add new counter")}
        content={
          <EquipmentCounterForm
            onSuccess={handleCounterAdded}
            equipmentId={id}
            hiddenFields={["equipment_id", "job"]}
          />
        }
      />
      <CustomModal
        opened={createJobOpened}
        setOpened={setCreateJobOpened}
        title={t("New job")}
        content={
          <JobForm
            onSuccess={handleJobCreated}
            initialClientId={equipmentClientId}
            initialEquipmentId={id}
          />
        }
      />
    </>
  );
};
